type ErrorWithMessage = { message: string };

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * Extracts a human-readable message from any unknown error value.
 *
 * Resolution order:
 * 1. null/undefined/falsy values — returns a generic fallback
 * 2. isErrorWithMessage - uses `message`.
 * 3. JSON stringification fallback
 * 4. String coercion fallback (e.g. circular references)
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }

  if (isErrorWithMessage(error)) {
    return error.message;
  }

  try {
    // JSON.stringify returns undefined for non-serializable values (functions, symbols).
    // String() is intentional here — produces useful output for those types.
    // oxlint-disable-next-line typescript/no-base-to-string
    return JSON.stringify(error) ?? String(error);
  } catch {
    // JSON.stringify threw (e.g. circular reference). String() is the only remaining option.
    // oxlint-disable-next-line typescript/no-base-to-string
    return String(error);
  }
}
